import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, Brain, User, Building2, Newspaper, Music2, Menu, X, ExternalLink,
  Zap, Globe, ChevronDown, Sparkles
} from "lucide-react";
import { ROUTE_PATHS, LANGUAGES, Language } from "@/lib/index";
import { useLanguage } from "@/lib/LanguageContext";
import { springPresets } from "@/lib/motion";

interface NavItem {
  key: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}

const NAV_ITEMS: NavItem[] = [
  { key: "optimizer", path: ROUTE_PATHS.HOME, icon: Home },
  { key: "studio", path: ROUTE_PATHS.STUDIO, icon: Sparkles },
  { key: "brew", path: ROUTE_PATHS.BREW, icon: Brain },
  { key: "giovanni", path: ROUTE_PATHS.GIOVANNI, icon: User },
  { key: "roi", path: ROUTE_PATHS.ROI, icon: Building2 },
  { key: "viewpoint", path: ROUTE_PATHS.VIEWPOINT, icon: Newspaper },
  { key: "ridmix", path: ROUTE_PATHS.RIDMIX, icon: Music2 },
];

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[2];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/50"
        style={{ background: "oklch(0.10 0.03 235 / 0.92)", backdropFilter: "blur(16px)" }}>
        <div className="flex items-center justify-between px-4 py-3 max-w-screen-2xl mx-auto">
          {/* Logo */}
          <NavLink to={ROUTE_PATHS.HOME} className="flex items-center gap-3 group">
            <div className="relative w-10 h-10 rounded-full overflow-hidden ring-2 ring-primary/40 group-hover:ring-primary/80 transition-all duration-300">
              <img src="/logo.png" alt="LinkedIn Profile Optimizer" className="w-full h-full object-cover" />
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-bold text-foreground leading-tight">{t("home.title")}</p>
              <p className="text-xs font-semibold text-accent leading-tight">{t("home.subtitle")}</p>
            </div>
          </NavLink>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-primary/15 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`
                }
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
          </nav>

          {/* Lang Selector + Mobile Toggle */}
          <div className="flex items-center gap-2">
            {/* Language Dropdown */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/30 text-xs font-semibold border border-border/50 hover:bg-muted/50 transition-all"
              >
                <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                <span>{currentLang.label}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {langOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-40 rounded-xl border border-border/60 bg-card p-1 shadow-2xl z-50 overflow-hidden"
                    >
                      {LANGUAGES.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setLanguage(lang.code as Language);
                            setLangOpen(false);
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            language === lang.code ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                          }`}
                        >
                          <span className="text-lg">{lang.flag}</span>
                          {lang.label}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            <button
              className="xl:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={springPresets.gentle}
              className="xl:hidden overflow-hidden border-t border-border/50"
            >
              <div className="px-4 py-3 flex flex-col gap-1">
                {NAV_ITEMS.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        isActive
                          ? "bg-primary/15 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      }`
                    }
                  >
                    <item.icon className="w-4 h-4" />
                    {t(`nav.${item.key}`)}
                  </NavLink>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      {/* Content */}
      <main className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname + language}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={springPresets.gentle}
            className="h-full"
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 py-6 px-4"
        style={{ background: "oklch(0.10 0.03 235 / 0.9)" }}>
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="w-8 h-8 rounded-full" />
            <div>
              <p className="text-xs text-muted-foreground">
                {t("footer.optimizer")} — <span className="text-accent font-semibold">360Brew AI</span>
              </p>
              <p className="text-xs text-muted-foreground/60">{t("footer.by")}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground/50">
            <Zap className="w-3 h-3 text-primary/50" />
            <span>{t("footer.powered")}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function ScoreBadge({ score, size = "md" }: { score: number; size?: "sm" | "md" | "lg" }) {
  const color =
    score >= 80 ? "text-emerald-400" :
    score >= 60 ? "text-accent" :
    "text-destructive";
  const sizes = { sm: "text-sm font-bold", md: "text-lg font-bold font-mono", lg: "text-3xl font-extrabold font-mono" };
  return <span className={`${sizes[size]} ${color}`}>{score}%</span>;
}

export function ExternalLinkCard({
  href, title, description, icon: Icon, badge,
}: {
  href: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
}) {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block group"
      whileHover={{ y: -4 }}
      transition={springPresets.snappy}
    >
      <div className="rounded-xl border border-border/60 bg-card p-5 hover:border-primary/50 transition-all duration-200"
        style={{ boxShadow: "0 4px 20px oklch(0.10 0.03 235 / 0.5)" }}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
            <Icon className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">{title}</h3>
              {badge && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent/15 text-accent font-medium">{badge}</span>
              )}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
          </div>
          <ExternalLink className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary/60 transition-colors flex-shrink-0 mt-0.5" />
        </div>
      </div>
    </motion.a>
  );
}
